import { AppDispatch } from '../../app/store'
import { selectPatient } from '../../features/patient/PatientContextSlice'
import { patientsApi } from '../../services/patients'
import {
  fetchPrescription,
  resetScript,
  setDeliveryState,
  setIsPaperPrescription,
} from '../../pages/NewScriptPage/ScriptForm/PrescribedItem/PrescribedItemSlice'
import {
  ProductDetails,
  ProductSearchClient,
} from '../../pages/NewScriptPage/ScriptForm/ProductSearchContextSlice'
import { HIStatus, Patient, PrescribedItem, Prescription, RecordStatus } from '../../types'
import { PrescriptionDeliveryMethod } from '../../types/prescription-delivery-method'
import { dateToLocalDate } from '../../utils/date/dateToLocalDate'
import { transformPbsData } from '../../pages/NewScriptPage/ScriptForm/PrescribedItem/pbsDataTransformer'

type IssueSnapshotPatient = Prescription['issueSnapshot']['patient']

const mapSnapshotIhiRecords = (
  records: IssueSnapshotPatient['ihiRecords'] | undefined
): Patient['ihi_records'] => {
  if (!records?.length) {
    return undefined
  }

  return records.map((record) => ({
    ihi_number: record.ihiNumber,
    ihi_status: record.ihiStatus as HIStatus,
    record_status: record.recordStatus as RecordStatus,
    created_at: record.createdAt,
  }))
}

const mapIssueSnapshotPatientToPatient = (snapshot: IssueSnapshotPatient): Patient => ({
  id: snapshot.id,
  given_names: snapshot.givenNames ?? '',
  family_name: snapshot.familyName ?? '',
  gender: snapshot.gender ?? '',
  date_of_birth: snapshot.dateOfBirth ?? '',
  medicare_no: snapshot.medicareNo ?? '',
  medicare_irn: snapshot.medicareIrn ?? '',
  medicare_valid_to: snapshot.medicareValidTo ?? '',
  dva_file_no: snapshot.dvaFileNo ?? '',
  dva_card_colour: snapshot.dvaCardColour ?? '',
  ihi_number: snapshot.ihiNumber ?? '',
  ihi_status: snapshot.ihiStatus ?? '',
  record_status: snapshot.recordStatus ?? '',
  ihi_records: mapSnapshotIhiRecords(snapshot.ihiRecords),
  pbs_entitlement_no: snapshot.pbsEntitlementNo ?? '',
  pbs_safety_net_entitlement_card_holder: false,
  pbs_concession_card_holder: false,
  email: snapshot.email ?? '',
  phone_number: snapshot.phoneNumber ?? '',
  address_1: snapshot.address1 ?? '',
  address_2: snapshot.address2 ?? '',
  suburb: snapshot.suburb ?? '',
  postcode: snapshot.postcode ?? 0,
  state: snapshot.state ?? '',
  entitlement_no: snapshot.entitlementNo ?? '',
  concession_pension_no: snapshot.concessionPensionNo ?? '',
  ctg_flag: snapshot.ctgFlag ?? false,
  hospital_category: snapshot.hospitalCategory ?? false,
  hospital_provider_number: snapshot.hospitalProviderNumber,
  racfid: snapshot.racfid,
  atsi_status: snapshot.atsiStatus ?? undefined,
  paper_only: snapshot.paperOnly ?? false,
})

const mapPrescribedItemForRenew = (item: PrescribedItem): PrescribedItem => ({
  ...item,
  id: undefined,
  sequence: undefined,
  prescriptionDate: dateToLocalDate(),
  prescriberScriptNumber: '',
  phoneApprovalAuthorityNumber: '',
  state: 'Active',
  regulation24: false,
  emergencySupply: false,
  sendToPharmacy: '',
  prescribedItemReserved04: '',
  pbsDvaAuthorityNumber: undefined,
  streamlinedCode: '',
  controlledSubstanceReference: undefined,
  itemBarcode: undefined,
  doctorsNotes: item.doctorsNotes ?? item.doctorNotes ?? undefined,
  moleculeName: item.moleculeName ?? undefined,
})

const inferProductType = (item: PrescribedItem): 'generic' | 'branded' =>
  item.genericIntention === 'G' ? 'generic' : 'branded'

const getMoleculeNameFromProduct = (product: ProductDetails): string | undefined =>
  product.vis?.[0]?.molecules?.[0]?.moleculeName

const applyPbsMaximumsFromProduct = (item: PrescribedItem): PrescribedItem => {
  if (!item.productDetails || !item.pbsCode || (!item.isPBS && !item.isRPBS)) {
    return item
  }

  const pbsConcise = item.productDetails.pbs?.find(({ pbsCode }) => pbsCode === item.pbsCode)
  if (!pbsConcise) {
    return item
  }

  const { maximumQuantity, maximumRepeats } = transformPbsData(pbsConcise)

  return {
    ...item,
    maximumQuantity,
    maximumRepeats,
  }
}

const hydratePrescriptionItemWithProductDetails = async (
  item: PrescribedItem
): Promise<PrescribedItem> => {
  if (item.productDetails) {
    return applyPbsMaximumsFromProduct(item)
  }

  try {
    const response = await ProductSearchClient.getProductDetails({
      id: item.productId,
      productType: inferProductType(item),
    })
    const product = response.product

    return applyPbsMaximumsFromProduct({
      ...item,
      productDetails: product,
      moleculeName: item.moleculeName ?? getMoleculeNameFromProduct(product),
    })
  } catch {
    return item
  }
}

const hydratePrescriptionItemsWithProductDetails = async (
  items: PrescribedItem[]
): Promise<PrescribedItem[]> =>
  Promise.all(items.map((item) => hydratePrescriptionItemWithProductDetails(item)))

const fetchPatientWithIhiForRenew = async (
  dispatch: AppDispatch,
  snapshot: IssueSnapshotPatient
): Promise<Patient> => {
  const mappedPatient = mapIssueSnapshotPatientToPatient(snapshot)
  try {
    const fetchedPatient = await dispatch(
      patientsApi.endpoints.getPatientById.initiate({
        patientId: snapshot.id,
      })
    ).unwrap()
    return fetchedPatient || mappedPatient
  } catch (error) {
    console.error('Error fetching patient:', error)
    return mappedPatient
  }
}

interface PrefillPrescriptionForRenewOptions {
  prescription: Prescription
  dispatch: AppDispatch
  defaultPrescriptionDeliveryMethod?: PrescriptionDeliveryMethod
}

export const prefillPrescriptionForRenew = async ({
  prescription,
  dispatch,
  defaultPrescriptionDeliveryMethod,
}: PrefillPrescriptionForRenewOptions): Promise<void> => {
  dispatch(resetScript())

  const snapshotPatient = prescription.issueSnapshot.patient
  const patient = await fetchPatientWithIhiForRenew(dispatch, snapshotPatient)
  dispatch(selectPatient(patient))
  const isPaperPrescription = !!patient.paper_only || prescription.isPaperPrescription
  dispatch(setIsPaperPrescription(isPaperPrescription))

  const mappedItems = prescription.prescribedItems.map(mapPrescribedItemForRenew)
  const hydratedItems = await hydratePrescriptionItemsWithProductDetails(mappedItems)
  dispatch(fetchPrescription(hydratedItems))

  if (isPaperPrescription) {
    dispatch(
      setDeliveryState({
        sendViaEmail: false,
        sendViaSMS: false,
        printPaperToken: true,
        recipientEmail: patient.email,
        recipientMobile: patient.phone_number,
      })
    )
    return
  }

  const deliveryMethod = defaultPrescriptionDeliveryMethod ?? PrescriptionDeliveryMethod.Paper_Token

  dispatch(
    setDeliveryState({
      sendViaEmail: deliveryMethod === PrescriptionDeliveryMethod.Email,
      sendViaSMS: deliveryMethod === PrescriptionDeliveryMethod.SMS,
      printPaperToken: deliveryMethod === PrescriptionDeliveryMethod.Paper_Token,
      recipientEmail: patient.email,
      recipientMobile: patient.phone_number,
    })
  )
}
